import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { SEO } from '../../components/SEO';
import { getApiBase } from '../../lib/apiBase';


const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mgopajrp';
const API_URL = getApiBase();

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export const ArchContact = () => {
  const [status, setStatus] = useState('idle');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [gdprError, setGdprError] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gdprConsent) { setGdprError(true); return; }
    setGdprError(false);
    setStatus('submitting');

    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });
      if (response.ok) {
        // Mirror to backend for admin panel tracking (fire-and-forget)
        try {
          await fetch(`${API_URL}/api/contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: data.get('name') || '',
              email: data.get('email') || '',
              structure_type: `[Architecture] ${data.get('subject') || 'General'}`,
              description: data.get('message') || '',
              consent_data_processing: true,
            }),
          });
        } catch { /* admin mirror is non-blocking */ }
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center" style={{ fontFamily: 'Manrope, sans-serif', background: 'var(--arch-bg)', color: 'var(--arch-text)' }}>
        <SEO title={`${t('arch.contact_heading')} | Durable Designs`} path="/architecture/contact" />
        <div className="text-center px-6" data-testid="arch-contact-success">
          <CheckCircle className="w-12 h-12 mx-auto mb-6" style={{ color: 'var(--arch-primary)' }} />
          <h2 className="text-2xl tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }}>{t('arch.contact_success_heading')}</h2>
          <p className="text-sm mb-8" style={{ color: 'var(--arch-text-secondary)', fontWeight: 300 }}>{t('arch.contact_success_desc')}</p>
          <Link
            to="/architecture"
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium tracking-wide text-white"
            style={{ background: 'var(--arch-primary)' }}
            data-testid="arch-contact-success-btn"
          >
            {t('arch.contact_success_btn')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" style={{ fontFamily: 'Manrope, sans-serif', background: 'var(--arch-bg)', color: 'var(--arch-text)' }}>
      <SEO title={`${t('arch.contact_heading')} | Durable Designs`} description={t('arch.contact_desc')} path="/architecture/contact" />

      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="arch-contact-hero">
        <div className="max-w-3xl">
          <span className="text-xs font-medium tracking-[0.2em] uppercase block mb-4" style={{ color: 'var(--arch-primary)' }}>{t('arch.contact_label')}</span>
          <h1 className="text-4xl sm:text-5xl tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400 }} data-testid="arch-contact-heading">{t('arch.contact_heading')}</h1>
          <p className="text-base" style={{ color: 'var(--arch-text-secondary)', fontWeight: 300 }}>{t('arch.contact_desc')}</p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-24 md:pb-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-16">
          {/* Form */}
          <motion.div className="lg:col-span-3" initial="hidden" animate="visible" variants={fadeUp}>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="arch-contact-form" noValidate>
              <input type="hidden" name="_replyto" value="architecture@durabledesigns.asia" />
              <input type="hidden" name="_subject" value={t('arch.contact_subject')} />

              <div>
                <label htmlFor="arch-name" className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: 'var(--arch-text-secondary)' }}>{t('arch.contact_name')} *</label>
                <input
                  id="arch-name" type="text" name="name" required
                  className="w-full px-4 py-3 text-sm bg-transparent outline-none transition-colors"
                  style={{ border: '1px solid var(--arch-border)', color: 'var(--arch-text)', fontFamily: 'Manrope, sans-serif' }}
                  data-testid="arch-contact-name"
                />
              </div>
              <div>
                <label htmlFor="arch-email" className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: 'var(--arch-text-secondary)' }}>{t('arch.contact_email')} *</label>
                <input
                  id="arch-email" type="email" name="email" required
                  className="w-full px-4 py-3 text-sm bg-transparent outline-none transition-colors"
                  style={{ border: '1px solid var(--arch-border)', color: 'var(--arch-text)', fontFamily: 'Manrope, sans-serif' }}
                  data-testid="arch-contact-email"
                />
              </div>
              <div>
                <label htmlFor="arch-service" className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: 'var(--arch-text-secondary)' }}>{t('arch.contact_service')}</label>
                <select
                  id="arch-service" name="subject"
                  className="w-full px-4 py-3 text-sm bg-transparent outline-none appearance-none"
                  style={{ border: '1px solid var(--arch-border)', color: 'var(--arch-text)', fontFamily: 'Manrope, sans-serif' }}
                  data-testid="arch-contact-service"
                >
                  <option value="">{t('arch.contact_select')}</option>
                  <optgroup label={t('arch.cat_corporate_label')}>
                    <option value="Lumion 3D Architectural Visualisation">{t('arch.corp_svc1_title')}</option>
                    <option value="Quantity Surveying & Bill of Quantities">{t('arch.corp_svc2_title')}</option>
                    <option value="Sustainable Commercial Fitout">{t('arch.corp_svc3_title')}</option>
                    <option value="Interior Space Planning & Design">{t('arch.corp_svc4_title')}</option>
                  </optgroup>
                  <optgroup label={t('arch.cat_public_label')}>
                    <option value="Playground & Community Space Design">{t('arch.pub_svc1_title')}</option>
                    <option value="Outdoor Furniture & Landscape Elements">{t('arch.pub_svc2_title')}</option>
                    <option value="Sustainable Material Specification">{t('arch.pub_svc3_title')}</option>
                    <option value="Concept Design & Feasibility">{t('arch.pub_svc4_title')}</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label htmlFor="arch-message" className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2" style={{ color: 'var(--arch-text-secondary)' }}>{t('arch.contact_message')} *</label>
                <textarea
                  id="arch-message" name="message" required rows="5"
                  className="w-full px-4 py-3 text-sm bg-transparent outline-none resize-none"
                  style={{ border: '1px solid var(--arch-border)', color: 'var(--arch-text)', fontFamily: 'Manrope, sans-serif' }}
                  data-testid="arch-contact-message"
                />
              </div>

              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox" name="gdpr-consent"
                    checked={gdprConsent}
                    onChange={(e) => { setGdprConsent(e.target.checked); if (e.target.checked) setGdprError(false); }}
                    required
                    className="mt-0.5 w-4 h-4 flex-shrink-0"
                    style={{ accentColor: 'var(--arch-primary)' }}
                    data-testid="arch-gdpr-checkbox"
                  />
                  <span className="text-xs leading-relaxed" style={{ color: 'var(--arch-text-secondary)' }}>
                    {t('arch.contact_gdpr')}{' '}
                    <Link to="/privacy-policy" className="underline" style={{ color: 'var(--arch-primary)' }}>{t('arch.contact_gdpr_link')}</Link>.
                  </span>
                </label>
                {gdprError && <p className="text-xs mt-2" style={{ color: '#DC2626' }} data-testid="arch-gdpr-error">{t('arch.contact_gdpr_error')}</p>}
              </div>

              {status === 'error' && (
                <p className="text-xs" style={{ color: '#DC2626' }} data-testid="arch-form-error">
                  {t('arch.contact_error')}{' '}
                  <a href="mailto:architecture@durabledesigns.asia" className="underline" style={{ color: 'var(--arch-primary)' }}>architecture@durabledesigns.asia</a>.
                </p>
              )}

              <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center gap-2 px-8 py-3 text-sm font-medium tracking-wide text-white transition-opacity disabled:opacity-50" style={{ background: 'var(--arch-primary)' }} data-testid="arch-contact-submit">
                {status === 'submitting' ? t('arch.contact_sending') : t('arch.contact_submit')} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </motion.div>

          {/* Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase block mb-3" style={{ color: 'var(--arch-primary)' }}>{t('arch.contact_company')}</span>
              <p className="text-base font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>{t('arch.contact_company_name')}</p>
            </div>
            <div>
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase block mb-3" style={{ color: 'var(--arch-primary)' }}>Email</span>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--arch-primary)' }} />
                <a href="mailto:architecture@durabledesigns.asia" className="text-sm font-medium" style={{ color: 'var(--arch-primary)' }} data-testid="arch-contact-email-link">architecture@durabledesigns.asia</a>
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase block mb-3" style={{ color: 'var(--arch-primary)' }}>Address</span>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--arch-primary)' }} />
                <p className="text-sm leading-relaxed" style={{ color: 'var(--arch-text-secondary)' }}>
                  Unit C, 8/F, King Palace Plaza,<br />No. 55 King Yip Street, Kwun Tong,<br />Kowloon, Hong Kong SAR, China
                </p>
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase block mb-3" style={{ color: 'var(--arch-primary)' }}>Telephone</span>
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--arch-primary)' }} />
                <div className="text-sm space-y-1" style={{ color: 'var(--arch-text-secondary)' }}>
                  <p>+852 5691 2587</p>
                </div>
              </div>
            </div>
            <div>
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase block mb-3" style={{ color: 'var(--arch-primary)' }}>Web</span>
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--arch-primary)' }} />
                <a href="https://durabledesigns.asia" target="_blank" rel="noopener noreferrer" className="text-sm" style={{ color: 'var(--arch-text-secondary)' }} data-testid="arch-contact-web">durabledesigns.asia</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArchContact;
